import { cva } from 'class-variance-authority';

/**
 * Avatar class recipe.
 *
 * Built from the Figma set (file 5aSkE0U4TeWbuIZDvccHjc, node 1872:19488, page `Avatar [In progress]`),
 * whose 36 cells are Size (20 / 24 / 32 / 40 / 56 / 64) x Content (Initials / Photo / Icon) x
 * Type (Person / Organisation), with three booleans on the Person cells — `speaking`, `onlineDot`
 * and `handRaised`. Content is not a prop here: it is derived from `src` and `name`, and a photo
 * that fails to load falls back to initials, so the axis is a state the component reaches rather
 * than one a caller picks.
 *
 * DISPLAY-ONLY, like Badge. No hover, pressed, focus or disabled read, because an Avatar is never
 * the interactive element — the row, card or tile around it is. AND NO MOTION: the photo appears
 * when it has loaded rather than fading in, and the speaking ring is static, because motion in
 * this library means "the system is working" (`docs/motion.md`).
 *
 * Like every recipe in this directory it carries ZERO `dark:` variants. Colour comes from the
 * `--ds-avatar-*` slots, which `.ds-avatar` in `components/styles/design-system.css` sets from DS
 * 2.0 semantic roles that flip with the theme on their own.
 *
 * Geometry, read off the set. 20–64 are spacing steps; 72 is `--spacing(18)`, one step past the
 * scale's end, because the dashboard profile draws it and 64 would shrink it by 8px. The per-size
 * values travel as custom properties so the dot and badge positions are one formula for all eight:
 *
 *   size   initials       icon   dot   hand (glyph)   organisation radius
 *   20     label-xs 11    12     8     14 (10)        radius/sm
 *   24     label-xs 11    12     8     14 (10)        radius/sm
 *   32     label-lg 14    16     8     14 (10)        radius/sm
 *   40     heading-xs 16  20     10    16 (12)        radius/sm
 *   56     heading-sm 20  28     12    20 (14)        radius/md
 *   64     heading-md 24  32     12    20 (14)        radius/md
 *   72     heading-lg 30  36     12    20 (14)        radius/md
 *   fill   40% of height  50%    12    20 (14)        radius/md
 *
 * The initials take the NEAREST TYPE STYLE rather than a fixed percentage of the box (35–44%
 * across the six), because a raw size per step would be six font sizes the type scale does not
 * have. Two letters at 24px and below are illegible, so `Avatar` passes one letter there.
 */
export const avatarRecipe = cva(
  [
    'ds-avatar',
    'relative isolate inline-flex shrink-0 items-center justify-center select-none',
    'font-sans tabular-nums',

    // Slot reads. `.ds-avatar` (and `.ds-avatar-photo` while a photo is showing) supply them.
    'bg-[var(--ds-avatar-bg)] text-[var(--ds-avatar-fg)]',

    // The 1px rim, painted as an overlay rather than a border so it sits ON TOP of the photo: a
    // border would be covered by the image and let the fill fringe around its edge. Inside the
    // box, so the avatar is exactly its size, which is also how the set draws it (INSIDE stroke).
    "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:content-['']",
    'after:border after:border-solid after:border-[var(--ds-avatar-border)]',

    // The live speaker: 2px ring, 2px gap, static. The set draws it as a 2px inside stroke on a
    // box 4px bigger on every side, which is this outline exactly.
    'data-[speaking]:outline-2 data-[speaking]:outline-offset-2 data-[speaking]:outline-solid',
    'data-[speaking]:outline-[var(--ds-avatar-speaking)]',
  ],
  {
    variants: {
      size: {
        20: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(5)] text-label-xs',
        24: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(6)] text-label-xs',
        32: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(8)] text-label-lg',
        40: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(10)] text-heading-xs',
        56: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(14)] text-heading-sm',
        64: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(16)] text-heading-md',
        72: 'size-[var(--ds-avatar-size)] [--ds-avatar-size:--spacing(18)] text-heading-lg',
        // As tall as the container, square. The avatar is its own size container, so its children
        // can read its height as `cqh`: `--ds-avatar-size` is only ever substituted INSIDE the
        // avatar (the dot and badge positions), where `100cqh` resolves against the avatar itself.
        fill: 'aspect-square h-full [container-type:size] [--ds-avatar-size:100cqh]',
      },
      type: {
        person: 'rounded-full',
        organisation: 'rounded-sm',
      },
    },
    compoundVariants: [
      // 25% of the box would be 14 and 16px; the radius scale has 6 and 16, so the two large
      // squares take `radius/md` and the four small ones `radius/sm`, as the set binds them.
      { type: 'organisation', size: [56, 64, 72, 'fill'], class: 'rounded-md' },
    ],
    defaultVariants: { size: 32, type: 'person' },
  }
);

/**
 * The initials. Trimmed to cap height so the letters centre optically, not on the font's box.
 * At `fill` they are 40% of the avatar's height — there is no type style to snap to when the box
 * itself has no fixed size, so this is the one length in the component that is a proportion.
 */
export const avatarInitialsRecipe = cva('block [text-box:trim-both_cap_alphabetic]', {
  variants: { fill: { true: 'text-[length:40cqh]' } },
});

/**
 * The online dot, centred on the circle's 45° edge: the edge sits 14.6% of the size in from the
 * box corner, so the dot's inset is that minus half the dot. `z-10` keeps it above the rim
 * overlay; the avatar's `isolate` keeps that from leaking past the avatar itself.
 *
 * The 2px ring is a cut-out in the colour of whatever the avatar sits on. It reads
 * `--ds-avatar-surface` from an ancestor and falls back to `background/primary`, the card and
 * panel surface; set the property on a container that is some other colour.
 */
export const avatarDotRecipe = cva(
  [
    'absolute z-10 rounded-full',
    'size-[var(--ds-avatar-dot)]',
    'right-[calc(var(--ds-avatar-size)*0.146-var(--ds-avatar-dot)/2)]',
    'bottom-[calc(var(--ds-avatar-size)*0.146-var(--ds-avatar-dot)/2)]',
    'bg-[var(--ds-avatar-online)]',
    'outline-2 outline-solid outline-[var(--ds-avatar-surface,var(--background-primary))]',
  ],
  {
    variants: {
      size: {
        20: '[--ds-avatar-dot:--spacing(2)]',
        24: '[--ds-avatar-dot:--spacing(2)]',
        32: '[--ds-avatar-dot:--spacing(2)]',
        40: '[--ds-avatar-dot:--spacing(2.5)]',
        56: '[--ds-avatar-dot:--spacing(3)]',
        64: '[--ds-avatar-dot:--spacing(3)]',
        72: '[--ds-avatar-dot:--spacing(3)]',
        fill: '[--ds-avatar-dot:--spacing(3)]',
      },
    },
    defaultVariants: { size: 32 },
  }
);

/**
 * The hand-raised badge, on the SAME anchor as the online dot: centred on the circle's 45° edge.
 * One rule for every status mark, so a dot and a badge sit in the same place at every size — the
 * badge is simply bigger. At the small sizes that centring makes it overhang the box on its own.
 */
export const avatarHandRecipe = cva(
  [
    'absolute z-10 inline-flex items-center justify-center rounded-full',
    'size-[var(--ds-avatar-hand)]',
    'right-[calc(var(--ds-avatar-size)*0.146-var(--ds-avatar-hand)/2)]',
    'bottom-[calc(var(--ds-avatar-size)*0.146-var(--ds-avatar-hand)/2)]',
    'bg-[var(--ds-avatar-hand-bg)] text-[var(--ds-avatar-hand-fg)]',
    'outline-2 outline-solid outline-[var(--ds-avatar-surface,var(--background-primary))]',
  ],
  {
    variants: {
      size: {
        20: '[--ds-avatar-hand:--spacing(3.5)]',
        24: '[--ds-avatar-hand:--spacing(3.5)]',
        32: '[--ds-avatar-hand:--spacing(3.5)]',
        40: '[--ds-avatar-hand:--spacing(4)]',
        56: '[--ds-avatar-hand:--spacing(5)]',
        64: '[--ds-avatar-hand:--spacing(5)]',
        72: '[--ds-avatar-hand:--spacing(5)]',
        fill: '[--ds-avatar-hand:--spacing(5)]',
      },
    },
    defaultVariants: { size: 32 },
  }
);
